import React, { useEffect, useState } from 'react';

import type { ImportMBFileUploadOptions } from '@appTypes/esb';

import { useCampImport, useVendorImport } from '../../hooks';
import type { ZoneKind } from '../../types';
import AdvancementImportModalView from './AdvancementImportModalView';

interface AdvancementImportModalProps {
  open: boolean;
  onClose: () => void;
}

const AdvancementImportModal: React.FC<AdvancementImportModalProps> = props => {
  const { open, onClose } = props;

  const vendor = useVendorImport();
  const camp = useCampImport({ open });

  // The zone the user picked this session. A camp import already running when
  // the modal opened is not one, so it never hides the vendor zone.
  const [sessionKind, setSessionKind] = useState<ZoneKind | null>(null);
  // Staging doesn't set `sessionKind`: the vendor zone stays usable until Import
  const [stagedCampFile, setStagedCampFile] = useState<File | null>(null);

  useEffect(() => {
    if (open) return;
    setSessionKind(null);
    setStagedCampFile(null);
    vendor.reset();
    camp.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleVendorSelect = (file: File) => {
    setStagedCampFile(null);
    setSessionKind('vendor');
    vendor.upload(file);
  };

  const handleCampImport = (options: ImportMBFileUploadOptions) => {
    if (!stagedCampFile) return;
    setSessionKind('camp');
    setStagedCampFile(null);
    camp.upload(stagedCampFile, options);
  };

  const handleVendorCancel = () => {
    setSessionKind(null);
    vendor.cancel();
  };

  const handleCampCancel = async () => {
    setSessionKind(null);
    await camp.cancel();
  };

  return (
    <AdvancementImportModalView
      open={open}
      onClose={onClose}
      sessionKind={sessionKind}
      vendor={vendor}
      camp={camp}
      stagedCampFile={stagedCampFile}
      onVendorSelect={handleVendorSelect}
      onCampSelect={setStagedCampFile}
      onCampImport={handleCampImport}
      onCampDiscard={() => setStagedCampFile(null)}
      onVendorCancel={handleVendorCancel}
      onCampCancel={handleCampCancel}
    />
  );
};

export default AdvancementImportModal;
